import { Component, computed, inject, input, OnDestroy, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import {
  DatatableColumn,
  DatatableComponent,
  DatatableConfig,
  TableSelectionService,
} from '@vectronic/table';
import { CardComponent, ConfirmDeleteComponent, LayoutWithSidebarComponent } from '@vectronic/ui';
import { DialogService, FilterItemWithLabel, SnackbarService } from '@vectronic/util';
import { downloadScheduleJsonFile } from '../../helpers/schedule-json.helper';
import { ScheduleTemplatesProviderService } from '../../services/schedule-templates-provider.service';
import { EditScheduleTemplateComponent } from '../edit-schedule-template/edit-schedule-template.component';
import { ScheduleTemplateFilterComponent } from '../schedule-template-filter/schedule-template-filter.component';
import { ScheduleTemplateModalComponent } from '../schedule-template-modal/schedule-template-modal.component';

@Component({
  selector: 'rc-schedule-templates',
  templateUrl: './schedule-templates.component.html',
  styleUrls: ['./schedule-templates.component.scss'],
  imports: [
    DatatableComponent,
    MatButton,
    MatIcon,
    MatDialogModule,
    LayoutWithSidebarComponent,
    CardComponent,
    EditScheduleTemplateComponent,
    ScheduleTemplateFilterComponent,
  ],
})
export class ScheduleTemplatesComponent implements OnInit, OnDestroy {
  private scheduleTemplatesProviderService = inject(ScheduleTemplatesProviderService);
  private dialogService = inject(DialogService);
  private snackbarService = inject(SnackbarService);
  tableSelectionService = inject(TableSelectionService<string>);

  hideFilter = input<boolean>();
  hideUserId = input<boolean>();
  hideCustomerVisibility = input<boolean>();

  searchFilter = this.scheduleTemplatesProviderService.searchFilter;

  availableUserAccounts = computed<FilterItemWithLabel<number>[]>(() => {
    return this.scheduleTemplatesProviderService.userAccounts();
  });

  rows = computed<dsDtos.ScheduleTemplateAdmin[]>(
    () =>
      (this.scheduleTemplatesProviderService.templates() as dsDtos.ScheduleTemplateAdmin[]) ?? [],
  );
  selectedTemplate = computed(() =>
    this.rows().find((r) => r.id === this.tableSelectionService.singleSelectionSignal()),
  );

  isSelectedReadonly = computed(() => {
    return (
      (this.selectedTemplate() as unknown as dsDtos.ScheduleTemplateCustomer)
        ?.templateFromVectronic && this.hideUserId()
    );
  });

  columns = computed<DatatableColumn<dsDtos.ScheduleTemplateAdmin>[]>(
    () =>
      [
        {
          fieldName: 'templateName',
          columnId: 1,
          headerName: 'Template Name',
          sticky: 'start',
          trim: 50,
        },
        {
          fieldName: 'schedule.scheduleType',
          columnId: 2,
          headerName: 'Schedule Type',
          translator: (row) => {
            switch (row.schedule?.scheduleType) {
              case 'GPS':
                return 'GPS';
              case 'VHF':
                return 'Beacon';
              case 'COMMUNICATION':
                return 'Communication';
              case 'CAMERA':
                return 'Camera';
              default:
                return row.schedule?.scheduleType;
            }
          },
        },
        !this.hideUserId()
          ? undefined
          : {
              fieldName: 'userAccountId',
              columnId: 5,
              headerName: 'Default',
              translator: (row) =>
                ((row as dsDtos.ScheduleTemplateCustomer).templateFromVectronic ??
                row.userAccountId === -1)
                  ? 'Yes'
                  : 'No',
            },
        this.hideCustomerVisibility()
          ? undefined
          : {
              fieldName: 'visibleForCustomers',
              columnId: 3,
              headerName: 'Visible For Customers',
              translator: (row) => {
                if (row.userAccountId === -1) {
                  return row.visibleForCustomers ? 'Yes' : 'No';
                }
                return '';
              },
            },
        this.hideUserId()
          ? undefined
          : {
              fieldName: 'userAccountId',
              columnId: 4,
              headerName: 'Temmplate Owner',
              disableStandardSearch: false,
              translator: (row) => {
                if (row.userAccountId === -1) return 'Admin';
                const userLabel = this.getUserLabel(row.userAccountId);
                return userLabel ?? row.userAccountId.toString();
              },
              cellHoverText: (row) => {
                if (row.userAccountId === -1) return 'Admin';
                const userEmail = this.getUserEmail(row.userAccountId);
                return (
                  userEmail ?? this.getUserLabel(row.userAccountId) ?? row.userAccountId.toString()
                );
              },
            },
      ].filter(Boolean) as DatatableColumn<dsDtos.ScheduleTemplateAdmin>[],
  );

  datatableConfig = computed<DatatableConfig<dsDtos.ScheduleTemplateAdmin>>(() => ({
    uniqueId: this.hideUserId() ? 'schedule-templates-preferences' : 'schedule-templates',
    title: 'Schedule Templates',
    columns: this.columns(),
    sort: { internal: true },
    compact: true,
    disableEditColumns: true,
    disableDragColumns: true,
    pagination: { internal: true, defaultSize: 25, showDataCount: true },
    search: true,
    select: { single: 'id' },
    rowActions: [
      {
        matIcon: 'download',
        label: 'Export Schedule JSON',
        onClick: (row) => this.exportScheduleTemplate(row),
        doDisable: (row) => !row.schedule?.scheduleType,
        disabledHoverText: 'Template has no schedule to export',
      },
      {
        matIcon: 'delete',
        label: 'Delete Template',
        onClick: (row) => this.deleteScheduleTemplate(row),
        color: 'warn',
        doDisable: (row) => {
          return (
            (row as unknown as dsDtos.ScheduleTemplateCustomer).templateFromVectronic &&
            this.hideUserId()
          );
        },
        disabledHoverText: 'Default template',
      },
    ],
  }));

  ngOnInit(): void {
    this.scheduleTemplatesProviderService.resetFilter();
  }

  openNewScheduleTemplateDialog() {
    this.tableSelectionService.deselectAll();
    this.dialogService.openWider(ScheduleTemplateModalComponent, {
      data: {
        scheduleTemplate: {} as dsDtos.ScheduleTemplateAdmin,
        hideCustomerVisibility: this.hideCustomerVisibility(),
        hideUserId: this.hideUserId(),
      },
      afterConfirm: (result) => {
        this.scheduleTemplatesProviderService
          .add(result)
          .subscribe(() =>
            this.snackbarService.success(`Template "${result.templateName}" saved successfully`),
          );
      },
    });
  }

  deleteScheduleTemplate(template: dsDtos.ScheduleTemplateAdmin | dsDtos.ScheduleTemplateCustomer) {
    this.dialogService.open(ConfirmDeleteComponent, {
      data: {
        title: 'Delete Schedule Template',
        bodyText: `Are you sure you want to delete the template "${template.templateName}"?`,
      },
      afterConfirm: () => {
        this.scheduleTemplatesProviderService.delete(template.id).subscribe();
      },
    });
  }

  exportScheduleTemplate(template: dsDtos.ScheduleTemplateAdmin) {
    if (!template.schedule?.scheduleType) {
      this.snackbarService.error('Template has no schedule to export.');
      return;
    }

    downloadScheduleJsonFile(template.schedule, template.templateName);
  }

  onSaveTemplate(template: dsDtos.ScheduleTemplateAdmin | dsDtos.ScheduleTemplateCustomer) {
    this.scheduleTemplatesProviderService
      .edit(template)
      .subscribe(() =>
        this.snackbarService.success(`Template "${template.templateName}" saved successfully`),
      );
  }

  onScheduleTypeChange(value: dsEnums.ScheduleType | undefined) {
    this.scheduleTemplatesProviderService.setScheduleType(value);
  }

  onTemplateNameChange(value: string) {
    this.scheduleTemplatesProviderService.setTemplateName(value);
  }

  onUserIdsChange(value: number[]) {
    this.scheduleTemplatesProviderService.setUserIds(value);
  }

  private getUserLabel(userId: number): string | undefined {
    const userAccount = this.scheduleTemplatesProviderService
      .userAccounts()
      .find((u) => u.item === userId);
    return userAccount?.label;
  }

  private getUserEmail(userId: number): string | undefined {
    const userAccount = this.scheduleTemplatesProviderService
      .userAccounts()
      .find((u) => u.item === userId);
    return userAccount?.email;
  }

  ngOnDestroy() {
    this.tableSelectionService.deselectAll();
  }
}
